import { computed, ref } from "vue";
import { useToast } from "vue-toastification";
import api from "@/api";
import { getAnalysesCount } from "@/api/scribe";
import { hasLuckyProductSubscription } from "@/helpers/subscription";
import type { Subscription } from "@/types/Subscription";
import { couldNot } from "@/helpers/userMessages";

const DEFAULT_FREE_REPORT_LIMIT = 30;

const getLuckyReportUsageLimit = (currentMe: MeContext): number => {
	const usageLimit = Number(currentMe.config?.luckyFreeReportLimit);
	if (!Number.isFinite(usageLimit) || usageLimit <= 0) {
		return DEFAULT_FREE_REPORT_LIMIT;
	}

	return Math.floor(usageLimit);
};

const isLuckyReportQuotaCheckEnabled = (): boolean => {
	return (
		String((window as any).env?.VITE_ENABLE_LUCKY_REPORT_QUOTA_CHECK || "false")
			.trim()
			.toLowerCase() === "true"
	);
};

export type MeContext = {
	config?: { luckyFreeReportLimit?: number };
	organizationSubscriptions?: Subscription[];
	organizationSubscription?: Subscription | null;
	organization?: {
		isTestAccount?: boolean;
	};
};

const shouldBypassUsageLimit = (currentMe: MeContext): boolean => {
	const subscriptions = currentMe?.organizationSubscriptions;
	const hasAnyLuckySubscription = Array.isArray(subscriptions) && hasLuckyProductSubscription(subscriptions);
	const subscriptionAccountType = String(currentMe?.organizationSubscription?.accountType || "")
		.trim()
		.toLowerCase();

	return (
		currentMe?.organization?.isTestAccount === true ||
		currentMe?.organizationSubscription === null ||
		subscriptionAccountType === "legacy" ||
		hasAnyLuckySubscription
	);
};

/**
 * Whether GET /me's answer lets the org run unlimited Sparky reports ("unlimited") or caps it at the free allowance
 * ("limited"). Pure: `load()` calls it once the quota check is enabled and /me has answered. Exported so contact-api's
 * server-side port (apps/contact-api/src/helpers/legacyAccess.ts, billing parity plan 11a) is pinned to this exact
 * decision by shared/types/fixtures/legacyLuckyReportCases.json
 * (tests/unit/components/scribe/composables/useLuckyReportQuotaSharedCases.spec.ts).
 */
export const decideLuckyReportAccess = (currentMe: MeContext): "unlimited" | "limited" => {
	if (shouldBypassUsageLimit(currentMe)) {
		return "unlimited";
	}

	// If subscription products are unavailable, do not block report access.
	if (!Array.isArray(currentMe?.organizationSubscriptions)) {
		return "unlimited";
	}

	return "limited";
};

export const useLuckyReportQuota = () => {
	const toast = useToast();
	const loading = ref(false);
	const totalReportsUsed = ref(0);
	const usageLimit = ref(DEFAULT_FREE_REPORT_LIMIT);
	const hasLuckySubscription = ref<boolean | null>(null);
	const billingRedirecting = ref(false);

	const isQuotaCheckEnabled = computed(() => isLuckyReportQuotaCheckEnabled());
	const isUsageLimited = computed(() => hasLuckySubscription.value === false);
	const hasReachedLimit = computed(() => isUsageLimited.value && totalReportsUsed.value >= usageLimit.value);
	const cappedReportsUsed = computed(() => Math.min(totalReportsUsed.value, usageLimit.value));
	const usageProgress = computed(() => Math.min(100, Math.round((cappedReportsUsed.value / usageLimit.value) * 100)));
	const isCheckingQuota = computed(() => isQuotaCheckEnabled.value && loading.value);

	const load = async () => {
		loading.value = true;
		try {
			if (!isQuotaCheckEnabled.value) {
				hasLuckySubscription.value = true;
				totalReportsUsed.value = 0;
				return;
			}

			const currentMe: MeContext = await api.getMe();
			usageLimit.value = getLuckyReportUsageLimit(currentMe);

			if (decideLuckyReportAccess(currentMe) === "unlimited") {
				hasLuckySubscription.value = true;
				totalReportsUsed.value = 0;
				return;
			}

			hasLuckySubscription.value = false;
			totalReportsUsed.value = await getAnalysesCount();
		} catch {
			// Fail open so users are not blocked by transient API issues.
			hasLuckySubscription.value = true;
			totalReportsUsed.value = 0;
		} finally {
			loading.value = false;
		}
	};

	const navigateToBilling = async () => {
		billingRedirecting.value = true;
		try {
			const billingPortalResponse = await api.getBillingPortalSessionUrl("subscription_update", window.location.toString());
			window.location = billingPortalResponse.url;
		} catch (error) {
			billingRedirecting.value = false;
			console.error("Failed to redirect to billing", error);
			toast.error(couldNot("open billing"));
		}
	};

	const refreshUsageCount = async () => {
		if (!isQuotaCheckEnabled.value) {
			hasLuckySubscription.value = true;
			totalReportsUsed.value = 0;
			return;
		}

		if (hasLuckySubscription.value === null) {
			await load();
			return;
		}

		if (!isUsageLimited.value) {
			return;
		}

		try {
			totalReportsUsed.value = await getAnalysesCount();
		} catch {
			// Keep previous usage if refresh fails; avoid blocking on transient errors.
		}
	};

	return {
		loading,
		totalReportsUsed,
		get usageLimit() {
			return usageLimit.value;
		},
		isQuotaCheckEnabled,
		isCheckingQuota,
		isUsageLimited,
		hasReachedLimit,
		cappedReportsUsed,
		usageProgress,
		billingRedirecting,
		load,
		refreshUsageCount,
		navigateToBilling
	};
};
